import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import type { PublicView, PlayerPublic, RoleId } from '@ringmaster/shared';
import { ROLE_META } from '@ringmaster/shared';
import { DESIGN_WIDTH, DESIGN_HEIGHT, SAFE, BroadcastFrame } from './BroadcastFrame.js';
import { ProsceniumFrame } from './ProsceniumFrame.js';
import { SceneStack } from '../fx/SceneStack.js';
import { scenesForGame } from '../assets/scenes/index.js';
import { useSequence } from '../fx/sequencer.js';
import {
  PlayerBust,
  TallyCounter,
  NameCard,
  Spotlight,
  type TallyRow,
  type SpotlightHandle,
} from './components/index.js';
import { ShakeContainer, BurstLayer, burst, type ShakeHandle } from '../fx/index.js';
import { centerInDesign, centerInViewport } from './components/coords.js';
import { Glyph } from '../ui/Glyph.js';
import { ornaments } from './ornaments.js';
import cardFront from '../assets/games/bloodmoon-card-front.webp';

/**
 * Stage VoteScreen — the flagship broadcast reveal (calibrates the quality bar).
 *
 * VOTE: living players stand as a center arc of PlayerBusts; the vignette tightens
 * on entry; each anonymous ballot lock-in flies a sealed-scroll token into a
 * ballot chest and sparks on impact; a "Ballots X/Y" scroll plate counts up. No
 * voter identity is ever shown during VOTE (only voteProgress counts exist in the
 * public view — the tally is withheld until RESOLVE_VOTE).
 *
 * RESOLVE_VOTE: the LYNCH_REVEAL / LYNCH_TIE choreography (useSequence, driven by
 * visualTuning). Curtain holds → busts dim → Spotlight snaps onto the condemned →
 * NameCard stamps in → role card flips → shake + faction burst (wolf = bloodBurst
 * + hard shake + gold rim on villagers; villager = somber goldDust + soft shake)
 * → TallyCounter counts up the per-voter rows. A tie scales the balances glyph,
 * fogs the gallows, and reads "No one hangs tonight".
 *
 * TWO EXPORTS:
 *  - VoteScreenContent({ view, isHost }) — the inner screen only. The live shell
 *    (StageView) provides BroadcastFrame + SceneStack + ProsceniumFrame around it,
 *    so it must NOT frame itself. Fills its positioned parent (absolute inset 0).
 *  - VoteScreenDemo({ view, isHost }) — wraps Content in the full frame stack FOR
 *    CARDLAB ONLY, so the demo shows the real composited result.
 */

export interface VoteScreenProps {
  view: PublicView;
  isHost: boolean;
}

// ── Geometry (1920×1080 design space) ─────────────────────────────────────────
const ARC_CY = 440; // vertical center of the candidate arc
const ARC_RADIUS_X = 620; // horizontal spread of the arc
const ARC_LIFT = 90; // how much the arc bows upward at its ends
const BUST_SIZE = 150;
// Ballot chest, design px. Kept above the reserved caption band (content must clear
// y≈920 = 1080 − 160 so the LowerThird plate never overlaps it): top 720, bottom 840.
const CHEST_POS = { x: DESIGN_WIDTH / 2, y: 780 };

/** mm:ss for the countdown (shared shape with DayScreen / StageHUD). */
function fmt(msLeft: number): string {
  const total = Math.max(0, Math.ceil(msLeft / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/** Place N busts along a shallow upward arc, centered. Returns design-space px. */
function arcPositions(n: number): Array<{ x: number; y: number }> {
  if (n <= 0) return [];
  return Array.from({ length: n }, (_, i) => {
    const t = n === 1 ? 0.5 : i / (n - 1); // 0..1
    const angle = (t - 0.5) * Math.PI; // -90°..+90°
    const x = DESIGN_WIDTH / 2 + Math.sin(angle) * ARC_RADIUS_X;
    const y = ARC_CY - Math.cos(angle) * ARC_LIFT;
    return { x, y };
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Inner screen
// ─────────────────────────────────────────────────────────────────────────────

function VoteScreenContentImpl({ view }: VoteScreenProps) {
  const reduced = useReducedMotion();
  const isResolve = view.phase === 'RESOLVE_VOTE';
  const lynch = view.lynchResult;

  const shakeRef = useRef<ShakeHandle>(null);
  const spotRef = useRef<SpotlightHandle>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  // Refs to each bust wrapper so we can locate the condemned one on screen.
  const bustEls = useRef<Map<string, HTMLDivElement>>(new Map());

  // The living players are the candidates. Order is stable (server order).
  const living = useMemo(() => view.players.filter((p) => p.alive), [view.players]);
  const positions = useMemo(() => arcPositions(living.length), [living.length]);

  const nameOf = useCallback(
    (id: string) => view.players.find((p) => p.id === id)?.name ?? id,
    [view.players],
  );

  // Condemned player (RESOLVE, non-tie).
  const condemned: PlayerPublic | null =
    isResolve && lynch && !lynch.tie && lynch.targetId
      ? (view.players.find((p) => p.id === lynch.targetId) ?? null)
      : null;
  const condemnedIsWolf = condemned?.revealedRole === 'WEREWOLF';

  // ── LYNCH sequences ─────────────────────────────────────────────────────────
  const isTie = !!(isResolve && lynch && (lynch.tie || !lynch.targetId));
  const revealActive = isResolve && !!lynch && !isTie;

  // Fire the FX beats (shake + burst) as their cues land. Reduced-motion keeps
  // the beat TIMING (room-wide dramaturgy) but suppresses the physical jolt/burst.
  const onReveal = useMemo(
    () => ({
      shake: () => {
        if (reduced) return;
        shakeRef.current?.shake(condemnedIsWolf ? 0.8 : 0.4);
      },
      burst: () => {
        if (reduced) return;
        fireFactionBurst(rootRef.current, bustEls.current.get(condemned?.id ?? ''), condemnedIsWolf);
      },
      spotlight: () => {
        const el = bustEls.current.get(condemned?.id ?? '');
        const target = centerInDesign(rootRef.current, el);
        if (target) spotRef.current?.snap({ ...target, r: 200 });
      },
    }),
    [reduced, condemnedIsWolf, condemned?.id],
  );

  const reveal = useSequence('LYNCH_REVEAL', { enabled: revealActive, onCue: onReveal });
  const tie = useSequence('LYNCH_TIE', { enabled: isTie });

  // Per-voter tally rows (only revealed at RESOLVE — the public view withholds
  // `votes` until then). Aggregate to per-candidate counts for the bars.
  const tallyRows: TallyRow[] = useMemo(() => {
    if (!lynch) return [];
    const counts = new Map<string, number>();
    for (const target of Object.values(lynch.votes)) {
      if (target === 'ABSTAIN') continue;
      counts.set(target, (counts.get(target) ?? 0) + 1);
    }
    return [...counts.entries()]
      .map(([id, votes]) => ({ id, name: nameOf(id), votes }))
      .sort((a, b) => b.votes - a.votes);
  }, [lynch, nameOf]);

  return (
    <ShakeContainer ref={shakeRef} style={{ position: 'absolute', inset: 0 }}>
      {/* Screen-local burst canvas (reveal impacts). */}
      <BurstLayer />

      <div ref={rootRef} style={{ position: 'absolute', inset: 0 }}>
        {!isResolve && <VotePhase view={view} living={living} positions={positions} bustEls={bustEls} reduced={!!reduced} />}

        {isResolve && lynch && !isTie && (
          <ResolveReveal
            living={living}
            positions={positions}
            bustEls={bustEls}
            condemned={condemned}
            condemnedIsWolf={condemnedIsWolf}
            reveal={reveal}
            tallyRows={tallyRows}
          />
        )}

        {isResolve && isTie && <ResolveTie tie={tie} />}
      </div>

      {/* Spotlight overlay (iris) — above busts, below nothing else. Only armed
          during a non-tie reveal; otherwise fully open (invisible). */}
      <Spotlight ref={spotRef} dark={revealActive ? 0.72 : 0} />
    </ShakeContainer>
  );
}

// ── VOTE phase ────────────────────────────────────────────────────────────────

function VotePhase({
  view,
  living,
  positions,
  bustEls,
  reduced,
}: {
  view: PublicView;
  living: PlayerPublic[];
  positions: Array<{ x: number; y: number }>;
  bustEls: React.MutableRefObject<Map<string, HTMLDivElement>>;
  reduced: boolean;
}) {
  const voted = view.voteProgress?.voted ?? 0;
  const total = view.voteProgress?.total ?? living.length;

  // Own countdown — VoteScreen is the phase hero (VOTE is in NO_HUD_PHASES, so the
  // shell HUD is suppressed here to avoid a double timer). Same gold/tabular style
  // as DayScreen's discussion clock. Server-driven deadline.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (view.timerEndsAt == null || view.paused) return;
    const id = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [view.timerEndsAt, view.paused]);
  const frozen = view.paused ? view.timerRemainingMs : null;
  const hasTimer = frozen != null || view.timerEndsAt != null;
  const left = frozen ?? (view.timerEndsAt != null ? view.timerEndsAt - now : 0);
  const urgent = !view.paused && left > 0 && left <= 30_000;

  // Vignette tightens ~10% over 2s on entry.
  const [tightened, setTightened] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTightened(true), 30);
    return () => clearTimeout(t);
  }, []);

  // One flying sealed-scroll token per ballot that has come in. We render `voted`
  // tokens; each new one animates from a frame edge into the chest. Anonymous —
  // tokens carry NO identity.
  const tokens = Array.from({ length: Math.min(voted, total) }, (_, i) => i);

  return (
    <>
      {/* Entry vignette that tightens. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(ellipse at 50% 42%, transparent ${tightened ? 46 : 56}%, rgba(0,0,0,0.55) 100%)`,
          transition: reduced ? 'none' : 'background 2000ms var(--ease-standard)',
          zIndex: 1,
        }}
      />

      {/* Header — VoteScreen's hero. Sits fully BELOW the proscenium valance (top
          ~16% of the frame) so it isn't clipped, in the band above the candidate arc.
          "The Vote" title + a big gold tabular countdown (matches DayScreen). Compact
          so the clock clears the top of the center candidate bust; an ink scrim keeps
          it legible over the bright day sky behind. */}
      <div
        style={{
          position: 'absolute',
          top: '15.5%',
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0,
          zIndex: 3,
        }}
      >
        {/* Ink scrim behind the header (day-sky VOTE scene is bright). */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: -18,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 560,
            height: 190,
            background:
              'radial-gradient(58% 54% at 50% 44%, rgba(5,3,5,0.7) 0%, rgba(5,3,5,0.42) 52%, rgba(5,3,5,0) 80%)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <div
          style={{
            position: 'relative',
            zIndex: 1,
            fontFamily: 'var(--font-display, serif)',
            fontSize: 46,
            lineHeight: 1.1,
            background: 'linear-gradient(180deg, var(--gold-6,#f7e3b0), var(--gold-3,#a9762f))',
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          The Vote
        </div>
        {hasTimer && (
          <div
            style={{
              position: 'relative',
              zIndex: 1,
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontSize: 64,
              fontWeight: 700,
              lineHeight: 1,
              fontVariantNumeric: 'tabular-nums',
              color: view.paused
                ? 'var(--text-tertiary, rgba(255,255,255,0.5))'
                : urgent
                  ? 'var(--blood-6, #ff5a4f)'
                  : 'var(--gold-6, #f7e3b0)',
              textShadow: urgent ? 'var(--rim-blood, 0 0 24px rgba(153,0,0,.55))' : 'none',
            }}
          >
            {fmt(left)}
          </div>
        )}
      </div>

      {/* Candidate arc */}
      {living.map((p, i) => {
        const pos = positions[i] ?? { x: DESIGN_WIDTH / 2, y: ARC_CY };
        return (
          <div
            key={p.id}
            ref={(el) => {
              if (el) bustEls.current.set(p.id, el);
              else bustEls.current.delete(p.id);
            }}
            style={{
              position: 'absolute',
              left: pos.x - BUST_SIZE / 2,
              top: pos.y - BUST_SIZE / 2,
              width: BUST_SIZE,
              zIndex: 2,
            }}
          >
            <PlayerBust player={p} size={BUST_SIZE} state="normal" />
          </div>
        );
      })}

      {/* Ballot chest + flying sealed-scroll tokens */}
      <BallotChest voted={voted} total={total} tokens={tokens} reduced={reduced} />
    </>
  );
}

/** The ballot chest plate + the sealed-scroll tokens flying in + the count. */
function BallotChest({
  voted,
  total,
  tokens,
  reduced,
}: {
  voted: number;
  total: number;
  tokens: number[];
  reduced: boolean;
}) {
  return (
    <>
      {/* Chest plate */}
      <div
        style={{
          position: 'absolute',
          left: CHEST_POS.x - 130,
          top: CHEST_POS.y - 60,
          width: 260,
          height: 120,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: 'rgba(21,13,15,0.78)', // --ink-2 translucent
          border: '2px solid var(--gold-3, #a9762f)',
          borderRadius: 'var(--radius-lg, 16px)',
          boxShadow: 'var(--rim-gold, 0 0 24px rgba(230,184,102,.5))',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Glyph name="scroll" tone="gold" size={40} />
          <span
            style={{
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontVariantNumeric: 'tabular-nums',
              fontSize: 44,
              fontWeight: 700,
              color: 'var(--gold-6, #f7e3b0)',
            }}
          >
            {voted}
            <span style={{ color: 'var(--gold-3, #a9762f)', fontSize: 30 }}> / {total}</span>
          </span>
        </div>
        <span
          style={{
            fontFamily: 'var(--font-ui, Inter, sans-serif)',
            fontSize: 18,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--gold-4, #c9923f)',
          }}
        >
          Ballots cast
        </span>
      </div>

      {/* Flying tokens — one per ballot in. Each enters once, from an alternating
          edge, and settles onto the chest. Anonymous (no identity on the token). */}
      <AnimatePresence>
        {tokens.map((i) => {
          const fromLeft = i % 2 === 0;
          return (
            <motion.div
              key={i}
              initial={
                reduced
                  ? { opacity: 0 }
                  : { opacity: 0, x: fromLeft ? -DESIGN_WIDTH / 2 : DESIGN_WIDTH / 2, y: -260, scale: 0.6, rotate: fromLeft ? -40 : 40 }
              }
              animate={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }}
              transition={{ duration: reduced ? 0.2 : 0.7, ease: [0.7, 0, 0.16, 1] }}
              onAnimationComplete={() => {
                if (reduced) return;
                // Spark at the chest on impact (normalized viewport coords).
                fireChestSpark();
              }}
              style={{
                position: 'absolute',
                left: CHEST_POS.x - 22 + ((i % 5) - 2) * 10,
                top: CHEST_POS.y - 78,
                zIndex: 3,
                pointerEvents: 'none',
              }}
            >
              <Glyph name="seal" tone="blood" size={44} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </>
  );
}

// ── RESOLVE: lynch reveal ─────────────────────────────────────────────────────

function ResolveReveal({
  living,
  positions,
  bustEls,
  condemned,
  condemnedIsWolf,
  reveal,
  tallyRows,
}: {
  living: PlayerPublic[];
  positions: Array<{ x: number; y: number }>;
  bustEls: React.MutableRefObject<Map<string, HTMLDivElement>>;
  condemned: PlayerPublic | null;
  condemnedIsWolf: boolean;
  reveal: ReturnType<typeof useSequence>;
  tallyRows: TallyRow[];
}) {
  const showName = reveal.has('name');
  const showRole = reveal.has('roleFlip');
  const showTally = reveal.has('tally');
  // Once the spotlight beat lands, non-condemned busts dim to 20%.
  const dimOthers = reveal.has('spotlight');
  // A wolf exposed → the surviving villagers get a triumphant gold rim.
  const villagerTriumph = reveal.has('burst') && condemnedIsWolf;

  return (
    <>
      {/* The arc, with the condemned held bright and others dimmed. */}
      {living.map((p, i) => {
        const pos = positions[i] ?? { x: DESIGN_WIDTH / 2, y: ARC_CY };
        const isCondemned = p.id === condemned?.id;
        const state = isCondemned ? 'spotlit' : dimOthers ? 'dim' : 'normal';
        // Villagers get a triumphant gold rim when a wolf is exposed.
        const triumphRim = villagerTriumph && !isCondemned && p.alive;
        return (
          <div
            key={p.id}
            ref={(el) => {
              if (el) bustEls.current.set(p.id, el);
              else bustEls.current.delete(p.id);
            }}
            style={{
              position: 'absolute',
              left: pos.x - BUST_SIZE / 2,
              top: pos.y - BUST_SIZE / 2,
              width: BUST_SIZE,
              zIndex: isCondemned ? 3 : 2,
              filter: triumphRim ? 'drop-shadow(0 0 18px rgba(230,184,102,0.6))' : 'none',
              transition: 'filter 400ms var(--ease-standard)',
            }}
          >
            <PlayerBust player={p} size={BUST_SIZE} state={state} />
          </div>
        );
      })}

      {/* Name + role card, centered upper area. */}
      <div
        style={{
          position: 'absolute',
          top: SAFE + 40,
          left: 0,
          right: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 24,
          zIndex: 4,
        }}
      >
        {showName && condemned && (
          <NameCard
            name={condemned.name}
            eyebrow="The village condemns"
            variant="condemned"
          />
        )}
        {showRole && condemned?.revealedRole && (
          <RoleFlipCard role={condemned.revealedRole} isWolf={condemnedIsWolf} />
        )}
      </div>

      {/* Tally, lower band. Anchored so the whole plate ends above the reserved
          caption band (bottom edge ≈ y=904, clearing the y≈920 line): bottom:176
          lifts it clear of the LowerThird plate that would otherwise overlap it. */}
      {showTally && tallyRows.length > 0 && (
        <div
          style={{
            position: 'absolute',
            left: '18%',
            right: '18%',
            bottom: 176,
            zIndex: 4,
            background: 'rgba(11,7,9,0.7)',
            borderTop: '2px solid var(--gold-4, #c9923f)',
            borderRadius: 'var(--radius-md, 12px)',
            padding: '24px 40px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              marginBottom: 16,
              fontFamily: 'var(--font-ui, Inter, sans-serif)',
              fontSize: 18,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--gold-4, #c9923f)',
            }}
          >
            <Glyph name="gallows" tone="gold" size={22} /> How the village voted
          </div>
          <TallyCounter
            rows={tallyRows}
            condemnedId={condemned?.id ?? null}
            durationMs={reveal.params.tallyCountMs ?? 1600}
          />
        </div>
      )}
    </>
  );
}

/** The condemned's role card: the ornate card-front art + role name, a simple
 *  Y-axis flip in (NOT the interactive HoloRoleCard). */
function RoleFlipCard({ role, isWolf }: { role: RoleId; isWolf: boolean }) {
  const reduced = useReducedMotion();
  const label = ROLE_META[role].label;
  return (
    <motion.div
      initial={reduced ? { opacity: 0 } : { opacity: 0, rotateY: 90 }}
      animate={{ opacity: 1, rotateY: 0 }}
      transition={{ duration: reduced ? 0 : 0.6, ease: [0.7, 0, 0.16, 1] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        transformStyle: 'preserve-3d',
        perspective: 800,
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 200,
          height: 280,
          borderRadius: 'var(--radius-lg, 16px)',
          overflow: 'hidden',
          border: `2px solid ${isWolf ? 'var(--blood-5, #c22f3e)' : 'var(--gold-4, #c9923f)'}`,
          boxShadow: isWolf ? 'var(--rim-blood, 0 0 24px rgba(153,0,0,.55))' : 'var(--rim-gold, 0 0 24px rgba(230,184,102,.5))',
          backgroundImage: `url(${cardFront})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
        }}
      >
        <div style={{ marginBottom: 20, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <Glyph name={isWolf ? 'wolfHead' : role === 'SEER' ? 'seerEye' : 'candle'} tone={isWolf ? 'blood' : 'gold'} size={48} />
          <span
            style={{
              fontFamily: 'var(--font-display, serif)',
              fontSize: 28,
              color: isWolf ? 'var(--blood-6, #ff5a4f)' : 'var(--gold-6, #f7e3b0)',
              letterSpacing: '0.06em',
            }}
          >
            {label}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

// ── RESOLVE: tie ──────────────────────────────────────────────────────────────

function ResolveTie({ tie }: { tie: ReturnType<typeof useSequence> }) {
  const reduced = useReducedMotion();
  const showVerdict = tie.has('verdict');
  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
        zIndex: 4,
      }}
    >
      <motion.div
        initial={reduced ? { opacity: 0 } : { opacity: 0, scale: 0.7 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: reduced ? 0 : 0.9, ease: [0.7, 0, 0.16, 1] }}
      >
        <Glyph name="scales" tone="gold" size={160} title="The vote is tied" />
      </motion.div>
      {showVerdict && (
        <NameCard name="No one hangs tonight" variant="neutral" />
      )}
    </div>
  );
}

// ── FX helpers ────────────────────────────────────────────────────────────────

/** Fire the faction burst at the condemned bust: wolf → bloodBurst, else goldDust. */
function fireFactionBurst(
  root: HTMLDivElement | null,
  bust: HTMLElement | undefined,
  isWolf: boolean,
): void {
  void root;
  const { x, y } = centerInViewport(bust);
  burst({ preset: isWolf ? 'bloodBurst' : 'goldDust', x, y, count: isWolf ? undefined : 60 });
}

/** Spark at the ballot chest (roughly frame-center-lower) on token impact. */
function fireChestSpark(): void {
  // The chest sits at ~50% width, ~72% height of the design frame (y≈780/1080);
  // that maps close enough to the same fraction of the viewport for a spark accent.
  burst({ preset: 'sparkBurst', x: 0.5, y: 0.72, count: 8 });
}

// ─────────────────────────────────────────────────────────────────────────────
// Exports
// ─────────────────────────────────────────────────────────────────────────────

/** Inner screen — the live shell provides the frame stack around it. */
export const VoteScreenContent = memo(VoteScreenContentImpl);

/**
 * CardLab / standalone wrapper — mounts the full frame stack (BroadcastFrame +
 * SceneStack VOTE scene + ProsceniumFrame) so the demo shows the composited
 * result. Passes the FULL proscenium ornaments (valance + curtains) so this preview
 * matches the live StageView — the VOTE header must clear the valance, only visible
 * when the valance is rendered here too. The live StageView must use
 * VoteScreenContent to avoid double-framing.
 */
export function VoteScreenDemo({ view, isHost }: VoteScreenProps) {
  const manifest = scenesForGame('bloodmoon');
  const framed = (
    <ProsceniumFrame cornerSrc={ornaments.filigreeCorner} grainSrc={ornaments.grainTile}>
      <VoteScreenContent view={view} isHost={isHost} />
    </ProsceniumFrame>
  );
  return (
    <BroadcastFrame>
      {manifest ? (
        <SceneStack manifest={manifest} phase={view.phase}>
          {framed}
        </SceneStack>
      ) : (
        framed
      )}
    </BroadcastFrame>
  );
}
